import { ITextFieldProps, SpinnerSize, Stack, TextField, Label, Icon, MaskedTextField } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { Control, Controller, FieldErrors, ValidationRule } from "react-hook-form";

import { useDelayedState } from "../../hooks/use-delayed-state/useDelayedState";
import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { TextFieldClearButton } from "../inputs/button/TextFieldClearButton";
import { Spinner } from "../progress/Spinner";
import { TooltipHost, TooltipHostProps } from "../surfaces/tooltip/TooltipHost";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export type FormTextFieldProp = Pick<ITextFieldProps, "multiline" | "disabled"> & {
    name: string;
    label: string;
    placeholder?: string;
    control: Control;
    errors: FieldErrors;
    isLoading?: boolean;
    dependsOn?: string[];
    rules?: FormTextFieldRules;
    height?: number;
    tooltipContent?: TooltipHostProps["content"];
    mask?: string;
    onInputChange?(inputValue?: string): void;
    onBlur?(inputValue?: string): void;
};

export const FormTextField: React.FunctionComponent<FormTextFieldProp> = ({
    name,
    label,
    placeholder,
    control,
    errors,
    isLoading,
    dependsOn,
    rules,
    height,
    tooltipContent,
    multiline,
    disabled,
    mask,
    onInputChange,
    onBlur
}: FormTextFieldProp) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (onInputChange) {
            onInputChange("");
        }
        clearWarnings();
    };

    if (dependsOn) {
        useFormWatch({
            name,
            control,
            dependsOn,
            onClear: handleOnChange
        });
    }

    const render = (renderProp: { value: string | undefined; onChange(value?: string): void }) => {
        const [delayedValue, inputValue, setInputValue] = useDelayedState(renderProp.value, {
            delayValueInMiliseconds: 100
        });

        useEffect(() => {
            renderProp.onChange(delayedValue);

            if (onInputChange) {
                onInputChange(delayedValue);
            }
        }, [delayedValue]);

        const clearButtonBackgroudColor = "transparent";
        const clearButtonId = `${name}_TextFieldClearButtonId`;

        const clearValue = () => {
            renderProp.onChange(undefined);

            if (onInputChange) {
                onInputChange(undefined);
            }
        };

        const handleBlur = (event?: React.FocusEvent<HTMLElement>) => {
            const relatedTarget = event?.relatedTarget as EventTarget & HTMLElement;

            if (relatedTarget?.id !== clearButtonId && onBlur) {
                onBlur(delayedValue);
            }
        };

        const handleInputChange = (
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
        ) => {
            clearWarnings();
            setInputValue(newValue);
        };

        const handleClearButtonClick = () => {
            clearValue();
            clearWarnings();
            handleBlur();
        };

        const renderClearButton = () => (
            <TextFieldClearButton
                disabled={!renderProp.value}
                onClick={handleClearButtonClick}
                id={clearButtonId}
                testId={`${props.name}-clear`}
            />
        );

        const renderSpinner = () => (
            <Stack verticalAlign="center" styles={{ root: { height: "100%" } }}>
                <Spinner size={SpinnerSize.small} />
            </Stack>
        );

        const renderSuffix = () => {
            return (
                <Stack verticalAlign="center" horizontal={true}>
                    {isLoading && renderSpinner()}
                    {!!renderProp.value && renderClearButton()}
                </Stack>
            );
        };

        const renderLabel = () => {
            return (
                <Stack>
                    <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="center">
                        <Label disabled={disabled}>{getFormLabelText(label, !!rules?.required)}</Label>

                        {tooltipContent && (
                            <TooltipHost content={tooltipContent}>
                                <Stack>
                                    <Icon iconName="Info" styles={{ root: { marginLeft: 5, cursor: "default" } }} />
                                </Stack>
                            </TooltipHost>
                        )}
                    </Stack>
                </Stack>
            );
        };

        const styles = {
            fieldGroup: { border: errors[name] && "2px solid red" },
            suffix: { padding: 0, background: clearButtonBackgroudColor, alignItems: "start" },
            root: { width: "100%" },
            field: { height: height }
        };

        const props = {
            ["data-testid"]: name,
            name: name,
            placeholder: placeholder,
            onRenderLabel: renderLabel,
            styles: styles,
            onRenderSuffix: renderSuffix,
            onChange: handleInputChange,
            value: inputValue || "",
            multiline: multiline,
            disabled: disabled,
            mask: mask
        };

        return (
            <Stack onBlur={handleBlur}>
                {mask ? (
                    <MaskedTextField {...props} data-testtype={"masked-text"} />
                ) : (
                    <TextField {...props} data-testtype={"text"} />
                )}
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={name}
                control={control}
                rules={{
                    ...rules,
                    validate: getValidate<string, FormTextFieldRules>(
                        rules,
                        control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={name} errors={errors} testId={`${name}-error`} />
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} testId={`${name}-warning`} />
        </Stack>
    );
};

export interface FormTextFieldRules extends FormValidationRules<string> {
    min?: string | number;
    maxLength?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
}
